/*
 * Copyright (c) 2020 Signum+ Software & Facility GmbH
 * Author: jahn@signum-plus.at
 */

import { Injectable, Injector } from '@angular/core';
import { ViewService } from '@angular-kit/view';
import {
  ActionModel,
  ContainerModel,
  Orientations,
  TableCellModel,
  TableModel,
  TextModel
} from '@typescript-kit/view';
import { TimeClockViewKey } from '../key/time-clock-view.key';
import { TimeClockShellViewService } from './time-clock-shell.view-service';
import { TimeClockIdleViewData, TimeClockIdleViewModel } from './time-clock-idle.view-model';
import { ChangedEvent } from '@typescript-kit/core';

@Injectable()
export class TimeClockIdleViewService extends ViewService<TimeClockIdleViewModel, ContainerModel> {
  private static readonly RESET_PIN_CODE_TIMEOUT = 5000;
  private resetPinCodeTimeoutHandle: any;

  constructor(injector: Injector, private readonly shellViewService: TimeClockShellViewService) {
    super(injector);
  }

  initialize(data?: TimeClockIdleViewData): void {
    this.shellViewService.viewModel.nfcKey = null;
    super.initialize(data && data instanceof TimeClockIdleViewModel ? data : new TimeClockIdleViewModel(data));
  }

  finalize(): void {
    this.clearResetPinCodeTimeout();
  }

  protected onViewModelPropertyChanged(event: ChangedEvent): void {
    if (event.changes.pinCode) {
      this.onPinCodeChanged(event);
    }
  }

  private onPinCodeChanged(event: ChangedEvent): void {
    this.refreshPinCodeContainer(this.componentModel.items.get('pinCode') as ContainerModel);
    if (this.viewModel.pinCode.length === 4) {
      this.shellViewService.viewModel.nfcKey = this.viewModel.pinCode;
    } else if (this.viewModel.pinCode.length > 0) {
      this.scheduleResetPinCode();
    }
  }

  private scheduleResetPinCode() {
    this.clearResetPinCodeTimeout();
    this.resetPinCodeTimeoutHandle = setTimeout(() => {
      this.resetPinCodeTimeoutHandle = null;
      this.viewModel.pinCode = '';
    }, TimeClockIdleViewService.RESET_PIN_CODE_TIMEOUT);
  }

  private clearResetPinCodeTimeout() {
    if (!this.resetPinCodeTimeoutHandle) {
      return;
    }
    clearTimeout(this.resetPinCodeTimeoutHandle);
    this.resetPinCodeTimeoutHandle = null;
  }


  protected createComponentModel(): ContainerModel {
    return new ContainerModel({
      items: [
        this.createHintTextModel(),
        this.createPinCodeContainer(),
        this.createPinCodeKeypad()
      ]
    });
  }

  private createHintTextModel(): TextModel {
    return new TextModel({
      tags: ['app-hint'],
      text: `${TimeClockViewKey.IDLE}/hint`
    });
  }

  private createPinCodeContainer(): ContainerModel {
    return new ContainerModel({
      key: 'pinCode', tags: ['app-pin-code-container'], orientation: Orientations.SIDE_BY_SIDE,
      items: [
        this.createPinCodeDigitModel(0),
        this.createPinCodeDigitModel(1),
        this.createPinCodeDigitModel(2),
        this.createPinCodeDigitModel(3),
      ]
    });
  }

  private refreshPinCodeContainer(model: ContainerModel) {
    model.items.forEach((digitModel: TextModel) => {
      this.refreshPinCodeDigitModel(digitModel);
    });
  }

  private createPinCodeDigitModel(index: number): TextModel {
    return new TextModel({
      key: index.toString(),
      tags: ['app-pin-code-digit'],
      text: '\u2022', // '•'
      isActive: !!this.viewModel.pinCode[index]
    });
  }

  private refreshPinCodeDigitModel(model: TextModel): void {
    model.isActive = !!this.viewModel.pinCode[model.key];
  }

  private createPinCodeKeypad(): TableModel {
    return new TableModel({
      key: 'keypad', tags: ['app-pin-code-keypad'],
      cells: [
        this.createKeypadTableCell(0, 0, '1', () => this.viewModel.pinCode += '1'),
        this.createKeypadTableCell(0, 1, '2', () => this.viewModel.pinCode += '2'),
        this.createKeypadTableCell(0, 2, '3', () => this.viewModel.pinCode += '3'),
        this.createKeypadTableCell(1, 0, '4', () => this.viewModel.pinCode += '4'),
        this.createKeypadTableCell(1, 1, '5', () => this.viewModel.pinCode += '5'),
        this.createKeypadTableCell(1, 2, '6', () => this.viewModel.pinCode += '6'),
        this.createKeypadTableCell(2, 0, '7', () => this.viewModel.pinCode += '7'),
        this.createKeypadTableCell(2, 1, '8', () => this.viewModel.pinCode += '8'),
        this.createKeypadTableCell(2, 2, '9', () => this.viewModel.pinCode += '9'),
        this.createKeypadTableCell(3, 0, '✕', () => this.viewModel.pinCode = ''),
        this.createKeypadTableCell(3, 1, '0', () => this.viewModel.pinCode += '0'),
        this.createKeypadTableCell(3, 2, '\u25C0', () => this.viewModel.pinCode = this.viewModel.pinCode.length > 0
          ? this.viewModel.pinCode.substring(0, this.viewModel.pinCode.length - 1)
          : this.viewModel.pinCode
        )
      ]
    });
  }

  private createKeypadTableCell(row: number, column: number, content: string, clickHandler: () => void) {
    return new TableCellModel({
      row, column,
      content: new ActionModel({
        tags: ['app-keypad-button'],
        content,
        onClick: (event, model) => {
          model.isFocused = false;
          model.isActive = true;
          setTimeout(() => model.isActive = false, 100);
          clickHandler();
        },
      })
    });
  }


}
